import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Product1 from "../Img/Product1.png";
import Product2 from "../Img/Product2.png";
import Product3 from "../Img/Product3.png";
import Product4 from "../Img/Product4.png";
import Product5 from "../Img/Product5.png";
import Product6 from "../Img/Product6.png";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import decryptCookieData from "./Functions/decryptCookieData";
import { Minus, Plus, Calendar, ArrowRight } from "lucide-react";
import HeightChart from "../Img/height-chart.png";
import PreBookModal from "./ProductCard/PreBookModal";

export const safeParseJson = (value, fallback = []) => {
  try {
    let parsed = typeof value === "string" ? JSON.parse(value) : value;
    if (typeof parsed === "string") parsed = JSON.parse(parsed); // handle double‑encoded
    return parsed || fallback;
  } catch {
    return fallback;
  }
};

const dummyReviews = [
  { user: "Amit", comment: "Excellent quality and fast delivery!" },
  { user: "Priya", comment: "Good product, packaging could be better." },
  { user: "Rohit", comment: "Totally worth the price. Highly recommend!" },
];

const isOutOfStock = (product) => {
  const status = (product.stock || "").toLowerCase();
  const totalStock = product.totalStock || 0;

  if (status === "out of stock") {
    return true;
  }

  if (status === "unlimited stock") {
    return false; // ✅ always available
  }

  if (status === "in stock" && (!totalStock || Number(totalStock) === 0)) {
    return true;
  }

  return false;
};

const isFullyBooked = (product) => {
  const today = new Date().toISOString().split("T")[0];
  const status = (product.stock || "").toLowerCase().trim();
  if (status === "out of stock") return false;
  if (status === "unlimited stock") return false;
  const activeBookings = product.activeBookings || 0;
  const totalStock = product.totalStock || 0;
  const availableFrom = product.availableFrom;

  return (
    activeBookings >= totalStock &&
    totalStock > 0 &&
    availableFrom &&
    availableFrom > today
  );
};

const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString("en-GB");
};

const getDeliveryDate = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + Number(days));
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }); // Example: "18 May 2025"
};

const TENURE_TO_PERIOD_LABEL = {
  "1 week": "per week",
  "1 month": "per month",
  "3 months": "per 3 months",
  "6 months": "per 6 months",
  "1 year": "per year",
};

const termToPeriodLabel = (term) => {
  if (!term) return "per month";
  const key = String(term).trim().toLowerCase();
  if (TENURE_TO_PERIOD_LABEL[key]) return TENURE_TO_PERIOD_LABEL[key];
  if (key.includes("week")) return key === "1 week" ? "per week" : `per ${String(term).trim()}`;
  if (key.includes("month")) {
    return key === "1 month" ? "per month" : `per ${String(term).trim()}`;
  }
  if (key.includes("year")) {
    return key === "1 year" ? "per year" : `per ${String(term).trim()}`;
  }
  return `per ${String(term).trim()}`;
};

const getSelectedTenureTerm = (
  attributes,
  selectedVariations,
  selectedVariation,
) => {
  const tenureAttr = (attributes || []).find(
    (a) =>
      /tenure/i.test(String(a.slug || "")) ||
      /tenure/i.test(String(a.name || "")),
  );
  if (tenureAttr?.slug && selectedVariations[tenureAttr.slug]) {
    return selectedVariations[tenureAttr.slug];
  }
  for (const val of Object.values(selectedVariations || {})) {
    if (typeof val === "string" && /week|month|year/i.test(val)) return val;
  }
  const variationAttrs = selectedVariation?.attributes || {};
  for (const [key, val] of Object.entries(variationAttrs)) {
    if (/tenure/i.test(key) && val) return val;
  }
  return null;
};

const getPeriodSuffix = (
  attributes,
  selectedVariations,
  selectedVariation,
) => {
  const term = getSelectedTenureTerm(
    attributes,
    selectedVariations,
    selectedVariation,
  );
  return `(${termToPeriodLabel(term)})`;
};

const ProductSinglePage = ({ setCartItemLength, fetchCart, cartButtons }) => {
  // const images = [Product1, Product2, Product3, Product4];
  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const startX = useRef(0);
  const isDragging = useRef(false);
  const [openSection, setOpenSection] = useState(null);
  const { id, name } = useParams();
  const [singleProduct, setProducts] = useState({});
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [selectedVariations, setSelectedVariations] = useState({});
  const url = process.env.REACT_APP_API_URL;
  const [pincode, setPincodes] = useState([]);
  const [enteredPincode, setEnteredPincode] = useState("");
  const [deliveryStatus, setDeliveryStatus] = useState(""); // success/failure message
  const [isDeliveryAvailable, setIsDeliveryAvailable] = useState(null);
  const [groupedModal, setShowGroupedModal] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState("description");
  const [checkoutForm, setCheckoutForm] = useState({
    name: "",
    mobile: "",
    email: "",
    description: "",
    cart_products: [],
    user_id: "",
    user_name: "",
    main_id: process.env.REACT_APP_PERFUME_ID,
  });
  const navigate = useNavigate();
  const [newReview, setNewReview] = useState("");
  const [showHeightChart, setShowHeightChart] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [preBookData, setPreBookData] = useState(null);

  let decodedName = "";
  let decodedId = "";
  const userData =
    JSON.parse(localStorage.getItem("2xgxgshdugyywerw12gh")) || "";
  try {
    decodedName = decodeURIComponent(atob(name));
    decodedId = decodeURIComponent(atob(id));
  } catch (e) {
    console.error("Invalid encoded params:", e.message);
    // Optional: redirect to 404 or show error state
  }

  const handleSubmitReview = () => {
    if (!newReview.trim()) return;
    // TODO: API call to save review
    setNewReview("");
  };

  const addToCart = async () => {
    // const userData = decryptCookieData('2xgxgshdugyywerw12gh');
    const isLoggedIn = !!userData?.user_id;
    const variation = selectedVariation || singleProduct;
    const baseProduct = singleProduct;

    const item = {
      product_id: variation.product_id || baseProduct?.product_id,
      product_sku: variation.sku || baseProduct?.sku,
      product_name: baseProduct?.title || variation.title,
      product_price: variation.price || baseProduct?.originalPrice,
      product_saleprice: variation.salePrice || baseProduct?.discountPrice,
      product_stockstatus: variation.stock || baseProduct?.stock,
      product_status: baseProduct?.status || "Active",
      product_image: variation.imageUrl || baseProduct?.image,
      product_gallery: baseProduct?.gallery || [],
      description: baseProduct?.description || "",
      short_description: baseProduct?.short_description || "",
      category: baseProduct?.category || "",
      product_type: baseProduct.product_type,
      variation_attributes: variation.attributes || {},
      typeofsimple: "",
      user_id: userData?.user_id || null,
      user_name: userData?.name || null,
      delivery_days: baseProduct.delivery_days || "",
      main_id: process.env.REACT_APP_PERFUME_ID,
      quantity: quantity,
    };

    try {
      if (isLoggedIn) {
        const response = await fetch(`${url}/cart`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(item),
        });
        const result = await response.json();

        if (!response.ok || result.isError) {
          toast.warn(result.msg || "Product already in cart");
        } else {
          await fetchCart(userData?.user_id);
          toast.success("Product added to cart");
        }
      } else {
        const existing = JSON.parse(localStorage.getItem("guest_cart")) || [];
        const existingIndex = existing.findIndex(
          (p) => p.product_sku === item.product_sku,
        );

        if (existingIndex !== -1) {
          existing[existingIndex].quantity += quantity; // ✅ increment quantity
        } else {
          existing.push({ ...item });
        }

        localStorage.setItem("guest_cart", JSON.stringify(existing));
        setCartItemLength(existing.length);
        toast.success("Product added to cart");
      }
    } catch (err) {
      console.error("Cart error:", err);
      toast.error("Something went wrong");
    }
  };

  const fetchPincodeData = async () => {
    try {
      const response = await fetch(
        `${url}/pincode/getall/${process.env.REACT_APP_PERFUME_ID}`,
      );
      const res = await response.json();
      setPincodes(res.pincodesData);
    } catch (error) {
      console.log(error);
    }
  };

  const handlePincodeCheck = () => {
    if (!enteredPincode || enteredPincode.length < 5) {
      setDeliveryStatus("Please enter a valid pincode");
      setIsDeliveryAvailable(null);
      return;
    }

    // ✅ If no pincodes are configured, allow delivery everywhere
    if (pincode.length === 0) {
      setDeliveryStatus(`Delivery available to ${enteredPincode}`);
      setIsDeliveryAvailable(true);
      return;
    }

    const isAvailable = pincode.some((p) => p.pincode === enteredPincode);
    if (isAvailable) {
      setDeliveryStatus(`Delivery available to ${enteredPincode}`);
      setIsDeliveryAvailable(true);
    } else {
      setDeliveryStatus(
        `Sorry, delivery is not available to ${enteredPincode}`,
      );
      setIsDeliveryAvailable(false);
    }
  };

  const handleVariationSelect = (slug, termName) => {
    const updatedSelections = {
      ...selectedVariations,
      [slug]: termName,
    };
    setSelectedVariations(updatedSelections);

    const matched = matchVariation(
      updatedSelections,
      singleProduct.variation || [],
    );
    if (matched) {
      setSelectedVariation(matched);

      const variationImages =
        matched.galleryImages?.map((url) => ({ url })) || [];

      // If variationImages is empty, fallback to main product image
      if (variationImages.length > 0) {
        setImages(variationImages);
      } else {
        const fallbackImages = [
          ...(singleProduct.image ? [{ url: singleProduct.image }] : []),
          ...(Array.isArray(singleProduct.gallery)
            ? singleProduct.gallery
            : []),
        ];
        setImages(fallbackImages);
      }
    }
  };

  const matchVariation = (selected, variations) => {
    return variations.find((variation) => {
      return Object.entries(selected).every(([slug, name]) =>
        Object.values(variation.attributes).includes(name),
      );
    });
  };

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  const handleThumbnailClick = (index) => {
    setIsFading(true);
    setTimeout(() => {
      setCurrentIndex(index);
      setIsFading(false);
    }, 300);
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const handlePrev = () => {
    setCurrentIndex(
      (prevIndex) => (prevIndex - 1 + images.length) % images.length,
    );
  };

  const handleDragStart = (e) => {
    startX.current = e.type.includes("touch")
      ? e.touches[0].clientX
      : e.clientX;
    isDragging.current = true;
  };

  const handleDragMove = (e) => {
    if (!isDragging.current) return;

    const moveX = e.type.includes("touch") ? e.touches[0].clientX : e.clientX;
    const diff = moveX - startX.current;

    if (diff > 50) {
      handlePrev();
      isDragging.current = false;
    } else if (diff < -50) {
      handleNext();
      isDragging.current = false;
    }
  };

  const handleDragEnd = () => {
    isDragging.current = false;
  };

  const singleProductdata = async () => {
    try {
      const response = await fetch(
        `${url}/grouped-product/single/${decodedId}`,
      );
      const res = await response.json();

      const safeParse = (value, fallback = []) => {
        try {
          return typeof value === "string"
            ? JSON.parse(value)
            : value || fallback;
        } catch (e) {
          return fallback;
        }
      };

      const parsed = {
        ...res,
        gallery: safeParse(res.gallery),
        specifications: safeParse(res.specifications),
        key_features: safeParse(res.key_features),
        variation: safeParse(res.variation),
        attribute: safeParse(res.attribute),
      };

      setProducts(parsed);
      const allImages = [];
      if (parsed.image) {
        allImages.push({ url: parsed.image });
      }
      if (Array.isArray(parsed.gallery)) {
        allImages.push(...parsed.gallery);
      }
      setImages(allImages);
    } catch (error) {
      console.error("Error fetching product:", error);
    }
  };

  useEffect(() => {
    singleProductdata();
    fetchPincodeData();
  }, []);

  useEffect(() => {
    if (singleProduct.product_id) {
      fetch(
        `${url}/payment/availability/${singleProduct.product_id}/${process.env.REACT_APP_PERFUME_ID}`,
      )
        .then((res) => res.json())
        .then(({ data }) => {
          if (data) {
            setProducts((prev) => ({
              ...prev,
              activeBookings: data.activeBookings || 0,
              availableFrom: data.availableFrom,
            }));
          }
        })
        .catch((err) => {
          console.warn("Availability fetch failed:", err);
        });
    }
  }, [singleProduct.product_id]);

  useEffect(() => {
    if (
      singleProduct.product_type === "Variable Product" &&
      singleProduct.variation?.length > 0
    ) {
      const firstVariation = singleProduct.variation[0];
      setSelectedVariation(firstVariation);
      const variationImages =
        firstVariation.galleryImages?.map((url) => ({ url })) || [];
      const combinedImages = [
        ...(singleProduct.image ? [{ url: singleProduct.image }] : []),
        ...(Array.isArray(singleProduct.gallery) ? singleProduct.gallery : []),
        ...variationImages,
      ];
      setImages(combinedImages);

      // Auto-select the matching attribute terms from the first variation
      const firstVariationSelections = {};
      Object.entries(firstVariation.attributes || {}).forEach(
        ([key, value]) => {
          // Find the attribute slug based on attribute name (since variation keys are names)
          const matchingAttr = singleProduct.attribute?.find(
            (attr) => attr.name === key,
          );
          if (matchingAttr) {
            firstVariationSelections[matchingAttr.slug] = value;
          }
        },
      );
      setSelectedVariations(firstVariationSelections);
    }
  }, [singleProduct]);

  const handleVariationChange = (variation) => {
    setSelectedVariation(variation);
    setImages(variation.galleryImages?.map((url) => ({ url: url })) || []);
  };

  const getDefaultCartButtons = () => {
    const getQuote = cartButtons.find(
      (b) => b.button_id === "getquote" && b.default,
    );
    const addToCart = cartButtons.find(
      (b) => b.button_id === "addtocart" && b.default,
    );
    const buyNow = cartButtons.find(
      (b) => b.button_id === "buynow" && b.default,
    );

    if (getQuote) return ["getquote"]; // If Get Quote is default, it overrides the rest

    const result = [];
    if (addToCart) result.push("addtocart");
    if (buyNow) result.push("buynow");
    return result;
  };

  const handleBuyNow = () => {
    const variation = selectedVariation || singleProduct;
    const baseProduct = singleProduct;

    const item = {
      product_id: variation.product_id || baseProduct?.product_id,
      product_sku: variation.sku || baseProduct?.sku,
      product_name: baseProduct?.title || variation.title,
      product_price: variation.price || baseProduct?.originalPrice,
      product_saleprice: variation.salePrice || baseProduct?.discountPrice,
      product_stockstatus: variation.stock || baseProduct?.stock,
      product_status: baseProduct?.status || "Active",
      product_image: variation.imageUrl || baseProduct?.image,
      product_gallery: baseProduct?.gallery || [],
      description: baseProduct?.description || "",
      short_description: baseProduct?.short_description || "",
      category: baseProduct?.category || "",
      product_type: baseProduct.product_type,
      variation_attributes: variation.attributes || {},
      typeofsimple: "",
      user_id: userData?.user_id || null,
      user_name: userData?.name || null,
      delivery_days: baseProduct.delivery_days || "",
      main_id: process.env.REACT_APP_PERFUME_ID,
      quantity: quantity,
    };

    const products = [item];
    let discount = 0;
    const totalSalePrice = products.reduce(
      (acc, p) => acc + Number(p.product_saleprice),
      0,
    );
    const totalBeforeDiscount = products.reduce(
      (acc, p) => acc + Number(p.product_price),
      0,
    );
    const totalAfterDiscount = (totalSalePrice - discount).toFixed(2);

    const checkoutDetails = {
      products,

      totalBeforeDiscount: totalBeforeDiscount,
      totalAfterDiscount,
    };

    localStorage.setItem("checkout_data", JSON.stringify(checkoutDetails));
    navigate("/checkout");
  };

const handleAskQuote = async () => {
    const userData = decryptCookieData("2xgxgshdugyywerw12gh");
    const isLoggedIn = !!userData?.user_id;
    const variation = selectedVariation || singleProduct;
    const baseProduct = singleProduct;

    const item = {
      product_id: variation.product_id || baseProduct?.product_id,
      product_sku: variation.sku || baseProduct?.sku,
      product_name: baseProduct?.title || variation.title,
      product_price: variation.price || baseProduct?.originalPrice,
      product_saleprice: variation.salePrice || baseProduct?.discountPrice,
      product_stockstatus: variation.stock || baseProduct?.stock,
      product_status: baseProduct?.status || "Active",
      product_image: variation.imageUrl || baseProduct?.image,
      product_gallery: baseProduct?.gallery || [],
      description: baseProduct?.description || "",
      short_description: baseProduct?.short_description || "",
      category: baseProduct?.category || "",
      product_type: baseProduct.product_type,
      variation_attributes: variation.attributes || {},
      typeofsimple: "",
      user_id: userData?.user_id || null,
      user_name: userData?.name || null,
      delivery_days: baseProduct.delivery_days || "",
      main_id: process.env.REACT_APP_PERFUME_ID,
      quantity: quantity,
    };
    const payload = {
      name: checkoutForm.name,
      mobile: checkoutForm.mobile,
      email: checkoutForm.email,
      description: checkoutForm.description,
      cart_products: [item],
      user_id: userData?.user_id,
      user_name: userData?.full_name || checkoutForm.name,
      main_id: process.env.REACT_APP_PERFUME_ID,
    };
    try {
      const response = await fetch(`${url}/payment/getquote`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      const res = await response.json();
      if (response.ok) {
        toast.success("Request sent. Our team will contact you shortly.");
        setShowCheckoutModal(false);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handlePreBook = (product) => {
    setPreBookData(product);
  };
  const defaultButtons = getDefaultCartButtons();

  const groupedProducts = safeParseJson(singleProduct.grouped_product);

  const periodSuffix = getPeriodSuffix(
    singleProduct.attribute,
    selectedVariations,
    selectedVariation,
  );

  return (
    <>
      <ToastContainer position="top-center" autoClose={3000} />
      <div className="psp-perfume">
        <div className="psp-main-thumb">
          <div className="psp-image-perfume">
            <button className="psp-controls chevron-left" onClick={handlePrev}>
              <i className="fa fa-chevron-left"></i>
            </button>

            <div className="psp-slider-container">
              <AnimatePresence mode="wait">
                <motion.img
                  key={currentIndex}
                  src={`${url}${images[currentIndex]?.url}`}
                  alt="Product"
                  className="main-image"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4, ease: "easeInOut" }}
                  drag="x"
                  dragConstraints={{ left: 0, right: 0 }}
                  onDragEnd={(event, info) => {
                    if (info.offset.x > 50) handlePrev();
                    if (info.offset.x < -50) handleNext();
                  }}
                />
              </AnimatePresence>
            </div>

            <button className="psp-controls chevron-right" onClick={handleNext}>
              <i className="fa fa-chevron-right"></i>
            </button>

            <button
              type="button"
              className="psp-height-chart-btn"
              onClick={() => setShowHeightChart(true)}
            >
              Height Chart
            </button>
          </div>

          {/* Thumbnail Images */}
          <div className="psp-thumbnails">
            {images.map((image, index) => (
              <motion.img
                key={index}
                src={`${url}${image.url}`}
                alt={`Thumbnail ${index + 1}`}
                className={`thumbnail ${currentIndex === index ? "active" : ""}`}
                onClick={() => setCurrentIndex(index)}
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.3 }}
              />
            ))}
          </div>
        </div>

        <div className="psp-content-perfume">
          <h2 className="psp-product-title">{singleProduct.title}</h2>
          {/* <p className="psp-discount-price">₹{singleProduct.discountPrice}</p>
                    <p className="psp-regular-price"><span className="psp-regular-line">MRP ₹{singleProduct.originalPrice}</span> <span className="psp-regular-discount">(Save ₹{singleProduct.originalPrice - singleProduct.discountPrice})</span></p> */}
          {/* <p className="psp-product-short-description" dangerouslySetInnerHTML={{ __html: singleProduct.short_description }}></p> */}
          {(selectedVariation?.salePrice > 0 ||
            singleProduct?.discountPrice > 0) && (
            <>
              <p className="psp-discount-price">
                ₹
                {selectedVariation
                  ? selectedVariation.salePrice
                  : singleProduct.discountPrice}
              </p>

              {(selectedVariation?.price > 0 ||
                singleProduct?.originalPrice > 0) && (
                <p className="psp-regular-price">
                  <span className="psp-regular-line">
                    MRP ₹
                    {selectedVariation
                      ? selectedVariation.price
                      : singleProduct.originalPrice}
                  </span>
                  <span className="psp-regular-discount">
                    (Save ₹
                    {selectedVariation
                      ? selectedVariation.price - selectedVariation.salePrice
                      : singleProduct.originalPrice -
                        singleProduct.discountPrice}
                    )
                  </span>
                  <span className="psp-period-suffix">{periodSuffix}</span>
                </p>
              )}
            </>
          )}

          {/* <div
                        className="psp-product-short-description"
                        dangerouslySetInnerHTML={{ __html: singleProduct.short_description }}
                    ></div> */}
          <div
            className="psp-product-short-description"
            dangerouslySetInnerHTML={{
              __html: singleProduct.short_description,
            }}
          />

          <div className="variation-circle-container">
            {singleProduct.attribute?.map((attr) => {
              const { slug, name, selectedTerms } = attr;

              return (
                <div key={attr.id} className="variation-section">
                  <p>{name?.charAt(0).toUpperCase() + name?.slice(1)}</p>
                  {slug === "color" && (
                    <div className="color-options">
                      {selectedTerms.map((term) => (
                        <div
                          key={term.id}
                          className={`color-circle ${selectedVariations[slug] === term.name ? "selected" : ""}`}
                          style={{ backgroundColor: term.name.toLowerCase() }}
                          title={term.name}
                          onClick={() => handleVariationSelect(slug, term.name)}
                        />
                      ))}
                    </div>
                  )}
                  {slug === "other" && (
                    <div className="image-options">
                      {selectedTerms.map((term) => {
                        const matchedVariation = singleProduct.variation?.find(
                          (variation) =>
                            variation.attributes?.[name] === term.name,
                        );

                        const imageUrl =
                          matchedVariation?.imageUrl ||
                          matchedVariation?.galleryImages?.[0] ||
                          singleProduct.image;

                        return (
                          <img
                            key={term.id}
                            src={`${url}${imageUrl}`}
                            alt={term.name}
                            className={`option-image-circle ${selectedVariations[slug] === term.name ? "selected" : ""}`}
                            onClick={() =>
                              handleVariationSelect(slug, term.name)
                            }
                          />
                        );
                      })}
                    </div>
                  )}

                  {!["color", "other"].includes(slug) && (
                    <div className="radio-options">
                      {selectedTerms.map((term) => {
                        const isSelected =
                          selectedVariations[slug] === term.name;
                        return (
                          <label
                            key={term.id}
                            className={`radio-box ${isSelected ? "selected" : ""}`}
                          >
                            <input
                              type="radio"
                              name={slug}
                              checked={isSelected}
                              onChange={() =>
                                handleVariationSelect(slug, term.name)
                              }
                            />
                            {term.name}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {singleProduct.product_type === "Grouped Product" && (
            <p
              className="hbtviewproduct-modaltext"
              onClick={() => setShowGroupedModal(true)}
            >
              View Product
            </p>
          )}

          {/* Grouped Products Section */}
          {singleProduct.product_type === "Grouped Product" && groupedModal && (
            <GroupedProductsModal
              products={groupedProducts}
              onAddToCart={addToCart}
              onClose={() => setShowGroupedModal(false)}
            />
          )}
          <div className="psp-cart-row">
            <div className="psp-quantity-wrapper">
              <label htmlFor="quantity" className="psp-quantity-label">
                Qty
              </label>
              <div className="psp-quantity-control">
                <button
                  className="psp-quantity-btn"
                  onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
                >
                  <Minus size={20} />
                </button>

                <input
                  id="quantity"
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => {
                    const val = Math.max(1, Number(e.target.value));
                    setQuantity(val);
                  }}
                  className="psp-quantity-input"
                />

                <button
                  className="psp-quantity-btn"
                  onClick={() => setQuantity((prev) => prev + 1)}
                >
                  <Plus size={20} />
                </button>
              </div>
            </div>

            {/* Action Button */}
{isOutOfStock(singleProduct) ? (
              <div className="psp-add-to-cart">
                <button className="psp-add-to-cart-btn disabled" disabled>
                  Out of Stock
                </button>
              </div>
            ) : isFullyBooked(singleProduct) ? (
              <div className="psp-add-to-cart">
                <button 
                  className="psp-add-to-cart-btn" 
                  onClick={() => handlePreBook(singleProduct)}
                >
                  Pre-Book
                </button>
              </div>
            ) : defaultButtons.includes("getquote") ? (
              <div
                className="psp-add-to-cart"
                onClick={() => setShowCheckoutModal(true)}
              >
                <button className="psp-add-to-cart-btn">Get Quote</button>
              </div>
            ) : defaultButtons.includes("buynow") ? (
              <div className="psp-add-to-cart" onClick={handleBuyNow}>
                <button className="psp-add-to-cart-btn">Buy Now</button>
              </div>
            ) : defaultButtons.includes("addtocart") ? (
              <div className="psp-add-to-cart" onClick={addToCart}>
                <button className="psp-add-to-cart-btn">Add to Cart</button>
              </div>
            ) : null}
          </div>

          {/* {defaultButtons.includes('addtocart') && (

                        <div className="psp-add-to-cart" onClick={addToCart}>
                            <button className="psp-add-to-cart-btn">Add to Cart</button>
                        </div>
                    )}

                    {defaultButtons.includes('buynow') && (
                        <div className="psp-add-to-cart" 
                        // onClick={buyNow}
                        >
                            <button className="psp-add-to-cart-btn">Buy Now</button>
                        </div>
                    )}

                    {defaultButtons.includes('getquote') && (
                        <div className="psp-add-to-cart" 
                        // onClick={() => setShowQuoteModal(true)}
                        >
                            <button className="psp-add-to-cart-btn">Get Quote</button>
                        </div>
                    )} */}

          {/* <div className="psp-add-to-cart" onClick={addToCart}>
                        <button className="psp-add-to-cart-btn">Add to Cart</button>
                    </div> */}

          <ul className="psp-disclaimer-notes">
            <li>Actual cycle may differ from the photo</li>
            <li>Monthly subscription available</li>
            <li>
              Please go through our <Link to="/faqs">FAQs</Link> before availing
              any of our services. By subscribing to Cycle Rental Services you are adhering
              to all our{" "}
              <Link to="/terms-and-conditions">Terms &amp; Conditions</Link> and{" "}
              <Link to="/privacy-policy">Privacy Policy</Link>.
            </li>
          </ul>

          <div className="psp-estimated-date">
            <div className="psp-estimated-input">
              <input
                type="number"
                placeholder="Enter your pincode"
                value={enteredPincode}
                onChange={(e) => setEnteredPincode(e.target.value)}
              />
              <button
                onClick={handlePincodeCheck}
                className="psp-estimated-btn"
              >
                Check
              </button>
            </div>
            <p
              className="psp-estimated-time"
              style={{
                marginTop: "10px",
                fontWeight: "bold",
                color: deliveryStatus.includes("not available")
                  ? "red"
                  : "green",
              }}
            >
              {deliveryStatus}
            </p>
            {/* <p className="psp-estimated-time">Estimated delivery by <strong>{getDeliveryDate(singleProduct.delivery_days)}</strong> </p> */}
            <p className="psp-estimated-time">
              Estimated delivery by{" "}
              <strong>{getDeliveryDate(singleProduct.delivery_days)}</strong>
            </p>
          </div>
          <div
            className="psp-extend-subscription"
            onClick={() => setShowExtendModal(true)}
          >
            <button className="psp-add-to-cart-btn">
              <Calendar size={18} className="btn-icon" />
              <span>Extend your subscription anytime</span>
              <ArrowRight size={18} className="btn-arrow" />
            </button>
          </div>
        </div>
      </div>
      <div className="psp-tabs-wrapper">
        {(singleProduct?.description ||
          singleProduct?.specifications?.length > 0 ||
          singleProduct?.key_features?.length > 0) && (
          <>
            <div className="psp-tab-headers">
              {singleProduct?.description && (
                <button
                  className={`psp-tab-button ${activeTab === "description" ? "active" : ""}`}
                  onClick={() => setActiveTab("description")}
                >
                  Description
                </button>
              )}
              {singleProduct?.specifications?.length > 0 && (
                <button
                  className={`psp-tab-button ${activeTab === "specs" ? "active" : ""}`}
                  onClick={() => setActiveTab("specs")}
                >
                  Specifications
                </button>
              )}
              {singleProduct?.key_features?.length > 0 && (
                <button
                  className={`psp-tab-button ${activeTab === "features" ? "active" : ""}`}
                  onClick={() => setActiveTab("features")}
                >
                  Key Features
                </button>
              )}

              <button
                className={`psp-tab-button ${activeTab === "reviews" ? "active" : ""}`}
                onClick={() => setActiveTab("reviews")}
              >
                Reviews
              </button>
            </div>

            <div className="psp-tab-content">
              {activeTab === "description" && singleProduct?.description && (
                <div
                  dangerouslySetInnerHTML={{
                    __html: singleProduct.description,
                  }}
                />
              )}

              {activeTab === "specs" &&
                singleProduct?.specifications?.length > 0 && (
                  <ul>
                    {singleProduct.specifications.map((spec, i) => (
                      <li key={i}>
                        <strong>{spec.label}:</strong> {spec.value}
                      </li>
                    ))}
                  </ul>
                )}

              {activeTab === "features" &&
                singleProduct?.key_features?.length > 0 && (
                  <ul>
                    {singleProduct.key_features.map((feature, i) => (
                      <li key={i}>{feature}</li>
                    ))}
                  </ul>
                )}

              {activeTab === "reviews" && (
                <div className="reviews-section">
                  <h3>Customer Reviews</h3>

                  {singleProduct?.reviews?.length > 0 ? (
                    <ul className="review-list">
                      {singleProduct.reviews.map((review, i) => (
                        <li key={i}>
                          <strong>{review.user}</strong>
                          <p>{review.comment}</p>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>No reviews yet. Be the first to write one!</p>
                  )}

                  {/* Write review */}
                  <div className="write-review">
                    <h4>Write a Review</h4>
                    <textarea
                      placeholder="Write your review..."
                      value={newReview}
                      onChange={(e) => setNewReview(e.target.value)}
                    />
                    <button onClick={handleSubmitReview}>Submit Review</button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {showHeightChart && (
        <div className="modal-overlayhbt" style={{ zIndex: 1000 }}>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              zIndex: 1001,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            onClick={() => setShowHeightChart(false)}
          >
            <div
              style={{
                position: "relative",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "95vw",
                height: "95vh",
              }}
            >
              <div style={{ position: "relative", display: "inline-block" }}>
                <img
                  src={HeightChart}
                  alt="Height Chart"
                  style={{
                    maxWidth: "95vw",
                    maxHeight: "95vh",
                    objectFit: "contain",
                  }}
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowHeightChart(false);
                  }}
                  style={{
                    position: "absolute",
                    top: "5px",
                    right: "5px",
                    background: "rgba(255, 255, 255, 0.8)",
                    color: "black",
                    border: "none",
                    borderRadius: "50%",
                    width: "30px",
                    height: "30px",
                    fontSize: "16px",
                    fontWeight: "bold",
                    cursor: "pointer",
                    zIndex: 20,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                  }}
                >
                  &times;
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showCheckoutModal && (
        <div className="checkout-modal-overlay">
          <div className="checkout-modal-content">
            <div className="group-modal-header">
              <h3>Enter Guest Details</h3>
              <span
                className="checkout-close"
                onClick={() => setShowCheckoutModal(false)}
              >
                <i className="fa-solid fa-xmark"></i>
              </span>
            </div>
            <form className="checkout-form">
              <div className="checkout-form-group">
                <label htmlFor="name">Full Name</label>
                <input
                  id="name"
                  type="text"
                  placeholder="Full Name"
                  value={checkoutForm.name}
                  onChange={(e) =>
                    setCheckoutForm((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="checkout-form-group">
                <label htmlFor="mobile">Phone Number</label>
                <input
                  id="mobile"
                  type="tel"
                  placeholder="Phone Number"
                  value={checkoutForm.mobile}
                  onChange={(e) =>
                    setCheckoutForm((prev) => ({
                      ...prev,
                      mobile: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="checkout-form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  id="email"
                  type="email"
                  placeholder="Email Address"
                  value={checkoutForm.email}
                  onChange={(e) =>
                    setCheckoutForm((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="checkout-form-group">
                <label htmlFor="description">Description</label>
                <input
                  id="description"
                  type="text"
                  placeholder="Description"
                  value={checkoutForm.description}
                  onChange={(e) =>
                    setCheckoutForm((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
              </div>

              <button
                type="button"
                className="checkout-submit-btn"
                onClick={handleAskQuote}
              >
                Ask Quote
              </button>
            </form>
          </div>
        </div>
      )}
      {preBookData && (
        <PreBookModal 
          product={preBookData} 
          onClose={() => setPreBookData(null)} 
        />
      )}
      {showExtendModal && (
        <div className="extend-modal-overlay">
          <div className="extend-modal-content">
            <div className="group-modal-header">
              <h3>Extend Your Subscription</h3>
              <span
                className="checkout-close"
                onClick={() => setShowExtendModal(false)}
              >
                <i className="fa-solid fa-xmark"></i>
              </span>
            </div>
            <div className="extend-modal-body">
              <p>
                You have an option of extending your subscription duration in
                future.
              </p>
              <ul>
                <li>
                  The same needs to be done prior to the end date of your
                  original duration, and will be subject to the availability of
                  the cycle.
                </li>
                <li>
                  If the cycle is already pre-booked by someone we will not be
                  able to extend your subscription, you can opt for a different
                  cycle in that case.
                </li>
              </ul>
              <button
                type="button"
                className="checkout-submit-btn"
                onClick={() => setShowExtendModal(false)}
                style={{ marginTop: "20px" }}
              >
                OK, noted
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductSinglePage;

function GroupedProductsModal({
  products = [],
  title = "Products",
  onAddToCart,
  onClose,
}) {
  if (!Array.isArray(products) || products.length === 0) return null;

  const normalized = products.map((p) => ({
    id: p.product_id || p.id,
    title: p.title || "Untitled",
    image: p.imageUrl || p.image || "/default-image.jpg",
    discountPrice: p.salePrice || p.discountPrice,
    originalPrice: p.price || p.originalPrice,
    inStock: (p.stock ?? p.totalStock ?? 0) > 0,
  }));

  return (
    <div className="modal-overlayhbt">
      <div className="modal-boxhbt">
        <div className="modal-headerhbt">
          <h2>{title}</h2>
          <button className="close-btnhbt" onClick={onClose}>
            &times;
          </button>
        </div>

        {/* <h3 className="section-titlehbt">Starters</h3> */}

        <div className="modal-listhbt">
          {normalized.map((p, idx) => (
            <motion.div
              key={p.id || idx}
              className="modal-itemhbt"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
            >
              <div
                className="indicatorhbt"
                style={{ backgroundColor: p.inStock ? "#3152f4" : "#3152f4" }}
              ></div>
              <div className="item-titlehbt">{p.title}</div>
              <img
                src={
                  p.image.startsWith("http")
                    ? p.image
                    : `${process.env.REACT_APP_API_URL}${p.image}`
                }
                alt={p.title}
                className="item-imagehbt"
              />
            </motion.div>
          ))}
        </div>
        <button
          className="primary-btnhbt"
          onClick={() => onAddToCart(products)}
        >
          Add to Cart
        </button>
      </div>
    </div>
  );
}
